import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, BookOpen, Sparkles, Target, Heart, Lightbulb, X, ChevronLeft, ChevronRight, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { mockTestimonials } from '../mock';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const { addToCart } = useCart();
  const [featuredBooks, setFeaturedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewBook, setPreviewBook] = useState(null);
  const [previewPages, setPreviewPages] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);

  useEffect(() => {
    loadFeaturedBooks();
  }, []);

  const loadFeaturedBooks = async () => {
    try {
      // Only fetch featured books to reduce data transfer
      const response = await axios.get(`${API_URL}/books`);
      const featured = response.data.filter(book => book.featured).slice(0, 4);
      setFeaturedBooks(featured);
    } catch (error) {
      // Set empty array on error so page still loads
      setFeaturedBooks([]);
    } finally {
      setLoading(false);
    }
  };

  const openPreview = useCallback(async (book) => {
    setPreviewBook(book);
    setPreviewPages([]);
    setPreviewPage(0);
    setPreviewLoading(true);
    const pages = [];
    for (let i = 1; i <= Math.min(6, book.pages || 6); i++) {
      try {
        const res = await axios.get(`${API_URL}/books/${book.id}/preview/${i}`, { responseType: 'blob' });
        pages.push(URL.createObjectURL(res.data));
      } catch { break; }
    }
    setPreviewPages(pages);
    setPreviewLoading(false);
  }, []);

  const closePreview = useCallback(() => {
    previewPages.forEach(url => URL.revokeObjectURL(url));
    setPreviewBook(null);
    setPreviewPages([]);
    setPreviewPage(0);
  }, [previewPages]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-yellow-50 to-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg font-medium">Loading Amazing Books...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section with How It Works */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-yellow-50 to-green-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10">
            {/* Left Column - Hero Text + How It Works */}
            <div className="flex flex-col h-full">
              {/* Hero Content */}
              <div className="space-y-4 flex-shrink-0">
                <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
                  <Sparkles className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm font-medium text-gray-700">Learning Hidden in Every Adventure</span>
                </div>
                
                <h1 className="text-3xl md:text-4xl font-bold leading-tight">
                  <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                    Discover, Dream, Learn
                  </span>
                  <br />
                  <span className="text-gray-800">One Adventure at a Time</span>
                </h1>
                
                <p className="text-base text-gray-600 leading-relaxed">
                  Transform learning into thrilling adventures! Our books blend education with excitement, making every page a magical discovery for curious minds.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all">
                    <Link to="/books">
                      Explore Books <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="border-2 hover:bg-gray-50">
                    <Link to="/activities">Free Activities</Link>
                  </Button>
                </div>
              </div>

              {/* How It Works */}
              <div className="pt-4 border-t border-gray-200 flex-grow flex flex-col mt-6">
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  How It Works
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Get started in three simple steps
                </p>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-lg font-bold text-blue-600">1</span>
                    </div>
                    <h3 className="font-bold text-gray-800 mb-1 text-xs">Choose Your Option</h3>
                    <p className="text-xs text-gray-600">
                      Select books or subscribe
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-lg font-bold text-purple-600">2</span>
                    </div>
                    <h3 className="font-bold text-gray-800 mb-1 text-xs">Complete Purchase</h3>
                    <p className="text-xs text-gray-600">
                      Secure checkout with coupons
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-lg font-bold text-green-600">3</span>
                    </div>
                    <h3 className="font-bold text-gray-800 mb-1 text-xs">Start Learning</h3>
                    <p className="text-xs text-gray-600">
                      Ships 2-3 days or instant!
                    </p>
                  </div>
                </div>

                <div className="mt-auto pt-4">
                  <Button asChild className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg w-full">
                    <Link to="/books#pricing">
                      View Pricing Options <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Right Column - Book Cover Images Only */}
            <div className="relative self-stretch flex flex-col">
              <div className="grid grid-cols-2 gap-4 h-full auto-rows-fr">
                {featuredBooks.slice(0, 4).map((book, idx) => (
                  <div
                    key={book.id}
                    className="transform hover:scale-105 transition-transform duration-300 bg-white rounded-lg overflow-hidden shadow-xl flex items-center justify-center"
                  >
                    <img
                      src={book.coverImage}
                      alt={book.title}
                      className="max-w-full max-h-full object-contain p-3"
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                ))}
              </div>
              <div className="absolute -z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-300 to-purple-300 rounded-full blur-3xl opacity-20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Adventures Section */}
      <section className="py-8 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
              Featured Adventures
            </h2>
            <p className="text-base text-gray-600">
              Start your journey with these bestselling titles
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {featuredBooks.slice(0, 4).map((book) => (
              <Card key={book.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
                <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" style={{ height: '200px' }}>
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-semibold text-gray-800 shadow">
                    ${book.price}
                  </div>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-bold text-gray-800 text-sm mb-2 line-clamp-2">{book.title}</h3>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star key={`star-${i}`} className={`h-3 w-3 ${i < Math.floor(book.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                      ))}
                    </div>
                    <span className="text-xs text-gray-600">({book.reviews})</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => addToCart(book)}
                      size="sm"
                      className="flex-1 text-xs"
                    >
                      Add to Cart
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openPreview(book)}
                      data-testid={`home-preview-${book.id}`}
                    >
                      <BookOpen className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-6">
            <Button asChild variant="outline" size="lg">
              <Link to="/books">
                View All Books <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
              Why Learning Smart?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every book is crafted to spark curiosity, ignite imagination, and foster a lifelong love of learning
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: 'Disguised Learning',
                description: 'Education seamlessly woven into thrilling adventures that kids love',
                color: 'blue'
              },
              {
                icon: Sparkles,
                title: 'Imagination First',
                description: 'Whimsical stories that transport young minds to magical worlds',
                color: 'yellow'
              },
              {
                icon: Heart,
                title: 'Values & Growth',
                description: 'Moral lessons and critical thinking embedded in every quest',
                color: 'green'
              }
            ].map((value) => (
              <Card key={value.title} className="border-2 hover:shadow-xl transition-shadow duration-300 group">
                <CardContent className="p-6 text-center">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-${value.color}-100 text-${value.color}-600 mb-4 group-hover:scale-110 transition-transform`}>
                    <value.icon className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-10 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
              What Parents Say
            </h2>
            <p className="text-lg text-gray-600">
              Real stories from families who've discovered the magic
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {mockTestimonials.slice(0, 3).map((testimonial) => (
              <Card key={testimonial.name} className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start the Adventure?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join thousands of families who've made learning an adventure
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              <Link to="/books">
                Browse All Books <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
              <Link to="/activities">Try Free Activities</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Book Preview Dialog */}
      <Dialog open={!!previewBook} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {previewBook && (
            <>
              <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 p-6 pb-4">
                <button onClick={closePreview} className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-200 z-10">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
                <div className="flex gap-5">
                  <img src={previewBook.coverImage} alt={previewBook.title} className="w-28 h-40 object-contain rounded-lg shadow-lg flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-gray-900 mb-1">{previewBook.title}</h2>
                    <p className="text-sm text-gray-600 mb-1">by {previewBook.author}</p>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">{previewBook.category}</span>
                      <span className="text-xs text-gray-500">{previewBook.ageRange}</span>
                      <span className="text-xs text-gray-500">{previewBook.pages} pages</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star key={`pstar-${i}`} className={`h-3.5 w-3.5 ${i < Math.floor(previewBook.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                      ))}
                      <span className="text-xs text-gray-500 ml-1">({previewBook.reviews})</span>
                    </div>
                    <div className="relative">
                      <div
                        className="max-h-28 border rounded-md p-2 bg-white/50"
                        style={{
                          overflowY: 'auto',
                          scrollbarWidth: 'auto',
                          scrollbarColor: '#9ca3af #e5e7eb',
                        }}
                      >
                        <p className="text-sm text-gray-700">{previewBook.description}</p>
                      </div>
                      {previewBook.description && previewBook.description.length > 150 && (
                        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white/90 to-transparent rounded-b-md pointer-events-none" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Preview Pages</h3>
                {previewLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : previewPages.length > 0 ? (
                  <div className="relative">
                    <div className="bg-gray-50 rounded-lg overflow-hidden border flex items-center justify-center" style={{ minHeight: '300px' }}>
                      {previewPage < previewPages.length ? (
                        <img src={previewPages[previewPage]} alt={`Page ${previewPage + 1}`} className="max-w-full max-h-[350px] object-contain" />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-center px-8 py-12">
                          <BookOpen className="h-14 w-14 text-blue-500 mb-4" />
                          <h3 className="text-xl font-bold text-gray-800 mb-2">Enjoying the story so far?</h3>
                          <p className="text-gray-600 mb-6 max-w-sm">Why not buy the book or subscribe to read the rest!</p>
                          <div className="flex flex-col gap-3 w-full max-w-xs">
                            <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700" onClick={() => { addToCart(previewBook); closePreview(); }}>
                              <ShoppingCart className="h-4 w-4 mr-2" /> Buy Now - ${previewBook.price}
                            </Button>
                            <Button asChild variant="outline" className="w-full">
                              <Link to="/books#pricing" onClick={closePreview}>View Subscription Options</Link>
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <Button variant="outline" size="sm" disabled={previewPage === 0} onClick={() => setPreviewPage(p => p - 1)}>
                        <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                      </Button>
                      <span className="text-sm text-gray-500">
                        {previewPage < previewPages.length ? `Page ${previewPage + 1} of ${previewPages.length + 1}` : 'Preview Complete'}
                      </span>
                      <Button variant="outline" size="sm" disabled={previewPage >= previewPages.length} onClick={() => setPreviewPage(p => p + 1)}>
                        Next <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 text-sm">Preview not available</div>
                )}
              </div>
              <div className="px-6 pb-5">
                <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700" onClick={() => { addToCart(previewBook); closePreview(); }}>
                  <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart - ${previewBook.price}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;